import { useCallback, useMemo, useState } from "react";

/**
 * Lista a la que se agregan elementos desde un control auxiliar. El formulario
 * la usa tres veces (estudiantes, libros y niveles) con el mismo
 * comportamiento y distinta forma de construir el elemento.
 *
 * Cada elemento lleva `seleccionado`. Los estudiantes que llegan del grupo se
 * marcan todos: el docente desmarca a los que no van, en vez de borrarlos, y
 * asi la lista del grupo siempre queda a la vista.
 *
 * @param {Function} transformar recibe el borrador y devuelve el elemento por
 *   agregar; devolver un valor falsy cancela la insercion.
 */
export const useListaEditable = (transformar = (valor) => valor) => {
  const [items, setItems] = useState([]);
  const [borrador, setBorrador] = useState("");

  // Sin argumento agrega lo que haya en el borrador, que es como trabajan
  // los campos de texto. El buscador de estudiantes, en cambio, entrega la
  // persona elegida y no usa borrador.
  const agregar = (valor = borrador) => {
    const item = transformar(valor);
    if (!item) return;

    setItems((previos) => [...previos, { ...item, seleccionado: true }]);
    setBorrador("");
  };

  const quitar = useCallback(
    (indice) => setItems((previos) => previos.filter((_, i) => i !== indice)),
    [],
  );

  const alternar = useCallback(
    (indice) =>
      setItems((previos) =>
        previos.map((item, i) =>
          // Lo fijo (el estudiante que solicita) no se puede desmarcar.
          i === indice && !item.fijo
            ? { ...item, seleccionado: !item.seleccionado }
            : item,
        ),
      ),
    [],
  );

  const limpiar = useCallback(() => {
    setItems([]);
    setBorrador("");
  }, []);

  // Cambia la lista completa, por ejemplo con los estudiantes que llegan del
  // grupo de la materia elegida. Todos entran marcados.
  const reemplazar = useCallback(
    (nuevos) => setItems(nuevos.map((item) => ({ ...item, seleccionado: true }))),
    [],
  );

  const seleccionados = useMemo(
    () => items.filter(({ seleccionado }) => seleccionado),
    [items],
  );

  return {
    items,
    seleccionados,
    borrador,
    setBorrador,
    agregar,
    quitar,
    alternar,
    limpiar,
    reemplazar,
  };
};
